import { Buttons, Overlay, Scroller, Slider, Video } from '@cian/slider';
import * as React from 'react';

import * as styles from './Carousel.css';
import { LazyImage } from './components/LazyImage';
import { PaginationDots } from './components/PaginationDots';
import { Pessimization } from './components/Pessimization';
import { Stub } from './components/Stub';
import { IPessimization } from './types';
import { EType, IMedia } from '../../repositories/dailyrent/entities/media/Media';

interface ICarouselProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  desktopControls?: boolean;
  media: IMedia[];
  pessimization?: IPessimization;
  slotSliderEnd?: React.ReactNode;
  mainImageAlt?: string;
  onSlideChange?(): void;
}

export const Carousel: React.FC<ICarouselProps> = ({
  containerRef,
  desktopControls,
  media: mediaRaw,
  pessimization,
  slotSliderEnd,
  mainImageAlt,
  onSlideChange,
}) => {
  const media = React.useMemo(() => {
    const media = mediaRaw;

    if (pessimization && media[0]) {
      media.unshift(media[0]);
    }

    return media;
  }, [mediaRaw, pessimization]);

  if (media.length === 0) {
    return <Stub pessimization={pessimization} slotEnd={slotSliderEnd} />;
  }

  return (
    <Slider onSlideChange={onSlideChange}>
      <Scroller>
        {media.map((m, index) => {
          const key = `slide_${index}`;
          let slide: React.ReactNode = null;

          switch (m.type) {
            case EType.Layout:
            case EType.Photo:
              slide = (
                <LazyImage
                  containerRef={containerRef}
                  key={key}
                  style={{ objectFit: m.type === EType.Layout ? 'contain' : undefined }}
                  src={m.previewUrl}
                  alt={mainImageAlt}
                />
              );
              break;
            case EType.Video:
              slide = <Video key={key} src={m.url} previewUrl={m.previewUrl} />;
              break;
          }

          if (index === 0) {
            if (pessimization) {
              return (
                <Pessimization key={key} pessimization={pessimization}>
                  {slide}
                </Pessimization>
              );
            }
          }

          return slide;
        })}
      </Scroller>
      <Overlay hideable={false}>
        <div className={styles['controls']}>
          {desktopControls && <Buttons />}
          <PaginationDots />
        </div>
      </Overlay>
      {slotSliderEnd}
    </Slider>
  );
};
